import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, Instagram, Send, Loader2, Copy, Check } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL = "ogajbhiye@gmail.com";
const PHONE_DISPLAY = "+91 95953 39579";
const PHONE_TEL = "+919595339579";
const WHATSAPP = "https://wa.me/919595339579?text=Hello%20Dr.%20Ojaswini%2C%20I%20would%20like%20to%20connect.";
const INSTAGRAM = "https://www.instagram.com/littleonesdrojaswini/";

const formatApiError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
};

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);

  const set = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    setErrors({ ...errors, [k]: undefined });
  };

  const validate = () => {
    const errs = {};
    if (form.name.trim().length < 2) errs.name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Please enter a valid email address.";
    if (form.subject.trim().length < 2) errs.subject = "Please add a subject.";
    if (form.message.trim().length < 10) errs.message = "Please write a message of at least 10 characters.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — your message has been sent. Dr. Ojaswini will respond soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(EMAIL);
      setCopied(true);
      toast.success("Email address copied to clipboard.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy — please note the email address.");
    }
  };

  const inputCls = (k) =>
    `w-full bg-[#FAF7F4] border rounded-xl px-4 py-3.5 text-sm text-[#383237] placeholder:text-[#8A7E88]/70 transition-colors focus:outline-none focus:border-[#70435F] focus:bg-white ${
      errors[k] ? "border-red-400" : "border-[#E7DED4]"
    }`;

  return (
    <section id="contact" className="py-24 sm:py-32 bg-[#FAF7F4] border-t border-[#E7DED4]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20">
        <Reveal>
          <Eyebrow>Contact</Eyebrow>
          <h2
            data-testid="contact-heading"
            className="font-display text-3xl sm:text-4xl lg:text-[42px] font-medium tracking-tight text-[#383237] leading-tight mt-5 mb-5"
          >
            Let's <em className="italic text-[#70435F]">connect</em>
          </h2>
          <p className="text-base text-[#695E67] leading-relaxed mb-10 max-w-md">
            For professional enquiries, collaborations, and child health education initiatives, connect with
            Dr. Ojaswini.
          </p>

          <div className="space-y-4 max-w-md">
            <div className="flex items-center gap-2">
              <a
                data-testid="contact-click-to-email"
                href={`mailto:${EMAIL}`}
                className="flex-1 flex items-center gap-4 bg-white border border-[#70435F]/10 rounded-2xl px-5 py-4 hover:border-[#E8C2CB] hover:shadow-card transition-all duration-300"
              >
                <span className="w-10 h-10 rounded-full bg-[#E8C2CB]/30 flex items-center justify-center shrink-0">
                  <Mail size={18} strokeWidth={1.6} className="text-[#70435F]" />
                </span>
                <span>
                  <span className="block text-[10px] uppercase tracking-[0.18em] font-semibold text-[#8A7E88]">Email</span>
                  <span className="block text-sm font-medium text-[#383237]">{EMAIL}</span>
                </span>
              </a>
              <button
                data-testid="contact-copy-email"
                onClick={copyEmail}
                aria-label="Copy email address"
                className="w-11 h-11 rounded-full border border-[#E7DED4] bg-white flex items-center justify-center text-[#70435F] hover:bg-[#E8C2CB]/25 transition-colors shrink-0"
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>

            <a
              data-testid="contact-click-to-call"
              href={`tel:${PHONE_TEL}`}
              className="flex items-center gap-4 bg-white border border-[#70435F]/10 rounded-2xl px-5 py-4 hover:border-[#E8C2CB] hover:shadow-card transition-all duration-300"
            >
              <span className="w-10 h-10 rounded-full bg-[#E8C2CB]/30 flex items-center justify-center shrink-0">
                <Phone size={18} strokeWidth={1.6} className="text-[#70435F]" />
              </span>
              <span>
                <span className="block text-[10px] uppercase tracking-[0.18em] font-semibold text-[#8A7E88]">Phone</span>
                <span className="block text-sm font-medium text-[#383237]">{PHONE_DISPLAY}</span>
              </span>
            </a>

            <a
              data-testid="contact-click-to-whatsapp"
              href={WHATSAPP}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 bg-white border border-[#70435F]/10 rounded-2xl px-5 py-4 hover:border-[#E8C2CB] hover:shadow-card transition-all duration-300"
            >
              <span className="w-10 h-10 rounded-full bg-[#E8C2CB]/30 flex items-center justify-center shrink-0">
                <Send size={18} strokeWidth={1.6} className="text-[#70435F]" />
              </span>
              <span>
                <span className="block text-[10px] uppercase tracking-[0.18em] font-semibold text-[#8A7E88]">WhatsApp</span>
                <span className="block text-sm font-medium text-[#383237]">Message on WhatsApp</span>
              </span>
            </a>

            <a
              data-testid="contact-instagram-link"
              href={INSTAGRAM}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 bg-white border border-[#70435F]/10 rounded-2xl px-5 py-4 hover:border-[#E8C2CB] hover:shadow-card transition-all duration-300"
            >
              <span className="w-10 h-10 rounded-full bg-[#E8C2CB]/30 flex items-center justify-center shrink-0">
                <Instagram size={18} strokeWidth={1.6} className="text-[#70435F]" />
              </span>
              <span>
                <span className="block text-[10px] uppercase tracking-[0.18em] font-semibold text-[#8A7E88]">Instagram</span>
                <span className="block text-sm font-medium text-[#383237]">@littleonesdrojaswini</span>
              </span>
            </a>
          </div>

          <p data-testid="contact-privacy-notice" className="text-xs text-[#8A7E88] leading-relaxed mt-8 max-w-md">
            Privacy notice: your details are used only to respond to your enquiry. Please do not share patient
            reports or sensitive medical information through this form. This is not an emergency service.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <form
            data-testid="contact-form"
            onSubmit={submit}
            noValidate
            className="bg-white border border-[#70435F]/10 rounded-3xl p-7 sm:p-9 shadow-card"
          >
            <h3 className="font-display text-2xl font-semibold text-[#70435F] mb-7">Send a message</h3>
            <div className="space-y-5">
              <div>
                <label htmlFor="contact-name" className="block text-xs uppercase tracking-[0.14em] font-semibold text-[#695E67] mb-2">
                  Name
                </label>
                <input
                  id="contact-name"
                  data-testid="contact-input-name"
                  type="text"
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your full name"
                  className={inputCls("name")}
                />
                {errors.name && <p data-testid="contact-error-name" className="text-xs text-red-500 mt-1.5">{errors.name}</p>}
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-xs uppercase tracking-[0.14em] font-semibold text-[#695E67] mb-2">
                  Email
                </label>
                <input
                  id="contact-email"
                  data-testid="contact-input-email"
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="you@example.com"
                  className={inputCls("email")}
                />
                {errors.email && <p data-testid="contact-error-email" className="text-xs text-red-500 mt-1.5">{errors.email}</p>}
              </div>
              <div>
                <label htmlFor="contact-subject" className="block text-xs uppercase tracking-[0.14em] font-semibold text-[#695E67] mb-2">
                  Subject
                </label>
                <input
                  id="contact-subject"
                  data-testid="contact-input-subject"
                  type="text"
                  value={form.subject}
                  onChange={set("subject")}
                  placeholder="What is this regarding?"
                  className={inputCls("subject")}
                />
                {errors.subject && <p data-testid="contact-error-subject" className="text-xs text-red-500 mt-1.5">{errors.subject}</p>}
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-xs uppercase tracking-[0.14em] font-semibold text-[#695E67] mb-2">
                  Message
                </label>
                <textarea
                  id="contact-message"
                  data-testid="contact-input-message"
                  rows={5}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Share the context of your enquiry..."
                  className={`${inputCls("message")} resize-none`}
                />
                {errors.message && <p data-testid="contact-error-message" className="text-xs text-red-500 mt-1.5">{errors.message}</p>}
              </div>
              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={sending}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#70435F] text-white px-7 py-4 rounded-full hover:bg-[#5A344C] transition-colors shadow-soft font-medium text-sm disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {sending ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} strokeWidth={1.7} />}
                {sending ? "Sending..." : "Send Message"}
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
